'use client';

import { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import { motion, AnimatePresence } from 'framer-motion';
import {
  TrendingUp,
  Users,
  PieChart,
  Globe,
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  Menu,
  X,
  Star,
  Check,
  ArrowRight,
  Shield,
  Smartphone,
  BarChart3,
  Lock,
  Headphones,
  Zap,
  BookOpen,
  Award,
  CreditCard,
  Clock,
  Search,
  GraduationCap,
  Video,
  Gauge,
  LineChart,
  Target,
  Quote,
  Play,
  Monitor,
  Wifi,
  ShieldCheck,
} from 'lucide-react';

/* ───────────────────────── DATA ───────────────────────── */

const tickers = [
  { symbol: 'EUR/USD', price: '1.0847', change: '+0.12%' },
  { symbol: 'GBP/USD', price: '1.2634', change: '+0.08%' },
  { symbol: 'USD/JPY', price: '149.82', change: '-0.15%' },
  { symbol: 'BTC/USD', price: '87,245', change: '+2.34%' },
  { symbol: 'ETH/USD', price: '3,421', change: '+1.87%' },
  { symbol: 'Gold', price: '2,987.50', change: '+0.45%' },
  { symbol: 'S&P 500', price: '5,842.3', change: '+0.32%' },
  { symbol: 'DAX', price: '18,472', change: '-0.21%' },
  { symbol: 'Tesla', price: '178.32', change: '+1.24%' },
  { symbol: 'Crude Oil', price: '78.34', change: '+0.67%' },
  { symbol: 'NVIDIA', price: '875.28', change: '+3.15%' },
  { symbol: 'SOL/USD', price: '142.80', change: '+3.12%' },
];

const stats = [
  { value: '10+', label: 'Years of Experience' },
  { value: '25K+', label: 'Active Traders' },
  { value: '180+', label: 'Instruments' },
  { value: '$2.4B', label: 'Monthly Volume' },
];

const features = [
  {
    icon: Gauge,
    title: 'Ultra-Tight Spreads',
    desc: 'From 0.0 pips on major pairs with deep institutional liquidity pools.',
  },
  {
    icon: Zap,
    title: 'Lightning Execution',
    desc: 'Average execution speed under 12ms. No requotes, no hidden costs.',
  },
  {
    icon: Shield,
    title: 'Segregated Funds',
    desc: 'Client funds held in top-tier European banks, fully segregated.',
  },
  {
    icon: Headphones,
    title: '24/5 Expert Support',
    desc: 'Dedicated account managers who understand markets, not scripts.',
  },
  {
    icon: Monitor,
    title: 'Multi-Platform',
    desc: 'Trade on WebTrader, MT4, MT5, or our mobile app — seamless sync.',
  },
  {
    icon: Target,
    title: 'Risk Management',
    desc: 'Built-in stop loss, take profit, and negative balance protection.',
  },
];

/* Candlestick data for chart */
const candles = [
  { o: 72, h: 78, l: 68, c: 76 },
  { o: 76, h: 82, l: 74, c: 80 },
  { o: 80, h: 85, l: 78, c: 74 },
  { o: 74, h: 79, l: 70, c: 77 },
  { o: 77, h: 84, l: 75, c: 82 },
  { o: 82, h: 88, l: 80, c: 86 },
  { o: 86, h: 90, l: 83, c: 84 },
  { o: 84, h: 89, l: 82, c: 88 },
  { o: 88, h: 94, l: 86, c: 92 },
  { o: 92, h: 96, l: 88, c: 90 },
  { o: 90, h: 95, l: 87, c: 94 },
  { o: 94, h: 100, l: 92, c: 98 },
  { o: 98, h: 102, l: 93, c: 96 },
  { o: 96, h: 101, l: 94, c: 100 },
  { o: 100, h: 106, l: 98, c: 104 },
  { o: 104, h: 108, l: 100, c: 102 },
  { o: 102, h: 107, l: 99, c: 106 },
  { o: 106, h: 112, l: 104, c: 110 },
  { o: 110, h: 114, l: 106, c: 108 },
  { o: 108, h: 115, l: 107, c: 114 },
];

const markets = [
  { name: 'Forex', instruments: '60+ pairs', spread: 'From 0.0 pips', icon: '💱' },
  { name: 'Stocks', instruments: '500+ CFDs', spread: 'From 0.1%', icon: '📈' },
  { name: 'Indices', instruments: '20+ indices', spread: 'From 0.4 pts', icon: '🏛️' },
  { name: 'Commodities', instruments: '15+', spread: 'From 0.03', icon: '🥇' },
  { name: 'Crypto', instruments: '30+ pairs', spread: 'From 0.1%', icon: '₿' },
];

const regulations = [
  { code: 'CY', name: 'CySEC', full: 'Cyprus Securities and Exchange Commission' },
  { code: 'AU', name: 'ASIC', full: 'Australian Securities & Investments Commission' },
  { code: 'CA', name: 'IIROC', full: 'Investment Industry Regulatory Org. of Canada' },
  { code: 'AE', name: 'ADGM', full: 'Abu Dhabi Global Markets' },
  { code: 'IE', name: 'CBI', full: 'Central Bank of Ireland' },
  { code: 'ZA', name: 'FSCA', full: 'Financial Sector Conduct Authority' },
  { code: 'JP', name: 'FFAJ', full: 'Financial Futures Association of Japan' },
];

const reviews = [
  {
    name: 'Alexander Braun',
    role: 'Professional Trader',
    avatar: 'AB',
    text: 'MeridianFX execution speed is on par with institutional platforms. Raw spreads on EUR/USD are consistently competitive, even during high-volatility events.',
    stars: 5,
    source: 'Trustpilot',
  },
  {
    name: 'Marie Johansson',
    role: 'Portfolio Manager',
    avatar: 'MJ',
    text: 'The multi-asset access from a single account is what sold me. Forex, commodities, and equity CFDs seamlessly — the charting tools are genuinely professional-grade.',
    stars: 5,
    source: 'Trustpilot',
  },
  {
    name: 'David Chen',
    role: 'Algorithmic Trader',
    avatar: 'DC',
    text: "The API is well-documented and the latency is excellent. I've migrated three algo strategies and the fill quality improvement was immediately measurable.",
    stars: 5,
    source: 'Trustpilot',
  },
  {
    name: 'Sofia Petrova',
    role: 'Risk Analyst',
    avatar: 'SP',
    text: 'Impressed by the transparency — no hidden fees, clear margin requirements, and the risk tools are built into the platform rather than afterthoughts.',
    stars: 5,
    source: 'Google',
  },
  {
    name: 'Thomas Müller',
    role: 'Day Trader',
    avatar: 'TM',
    text: 'Switched from my previous broker after comparing execution reports. MeridianFX consistently fills at the quoted price. Mobile app is surprisingly full-featured.',
    stars: 5,
    source: 'Trustpilot',
  },
  {
    name: 'Emma Richardson',
    role: 'Swing Trader',
    avatar: 'ER',
    text: 'What I appreciate most is the education and support. My account manager actually understands markets. The weekly webinars are incredibly high quality.',
    stars: 5,
    source: 'Google',
  },
];

const teamPhotos = [
  { role: 'Senior Analyst', gradient: 'from-[#0D9488]/80 to-[#115E59]', initials: 'JK' },
  { role: 'Head of Trading', gradient: 'from-[#EA580C]/80 to-[#9A3412]', initials: 'LM' },
  { role: 'Client Success', gradient: 'from-[#0D9488]/80 to-[#134E4A]', initials: 'AR' },
  { role: 'Risk Manager', gradient: 'from-[#F59E0B]/80 to-[#92400E]', initials: 'NK' },
];

const faqs = [
  {
    q: 'What is MeridianFX?',
    a: 'MeridianFX is a regulated online trading platform providing access to Forex, Stocks, Indices, Commodities and Crypto CFDs. We combine institutional-grade technology with competitive pricing and dedicated client support.',
  },
  {
    q: 'How do I open a trading account?',
    a: "Click 'Open Account', complete the registration form with your personal details, verify your identity (KYC), and fund your account. The entire process takes less than 10 minutes.",
  },
  {
    q: 'What is the minimum deposit?',
    a: 'Our Standard account requires a minimum deposit of $10,000. Higher-tier accounts offer progressively tighter spreads, dedicated support, and premium services.',
  },
  {
    q: 'Is my money safe?',
    a: 'Yes. Client funds are held in segregated accounts at top-tier banks, fully separated from company funds. We are regulated across 7 jurisdictions worldwide.',
  },
  {
    q: 'What platforms do you offer?',
    a: 'We offer WebTrader, MetaTrader 4, MetaTrader 5, and fully-featured mobile apps for iOS and Android. All platforms include advanced charting and real-time data.',
  },
  {
    q: 'How can I withdraw?',
    a: 'Withdrawals via bank wire, credit/debit card, or e-wallets. Processed within 1-2 business days with no fees on standard withdrawals.',
  },
];

const footerLinks: Record<string, { label: string; href: string }[]> = {
  'Forex Trading': [
    { label: 'What is Forex', href: '#' },
    { label: 'How to Trade', href: '#' },
    { label: 'Forex Spreads', href: '#' },
    { label: 'Currency Pairs', href: '#' },
  ],
  'CFD Trading': [
    { label: 'What Are CFDs', href: '#' },
    { label: 'Stock CFDs', href: '#' },
    { label: 'Gold Trading', href: '#' },
    { label: 'Oil Trading', href: '#' },
  ],
  Platforms: [
    { label: 'WebTrader', href: '#' },
    { label: 'MetaTrader 4', href: '#' },
    { label: 'MetaTrader 5', href: '#' },
    { label: 'Mobile App', href: '#' },
  ],
  Education: [
    { label: 'Trading Academy', href: '#' },
    { label: 'Webinars', href: '#' },
    { label: 'Market Analysis', href: '#' },
    { label: 'Economic Calendar', href: '#' },
  ],
  Company: [
    { label: 'About Us', href: '#' },
    { label: 'Regulation', href: '#' },
    { label: 'Awards', href: '#' },
    { label: 'Contact', href: '#' },
    { label: 'Legal', href: '#' },
  ],
};

/* ───────────────────────── HELPERS ───────────────────────── */

function CandlestickChart() {
  const w = 20,
    gap = 2;
  return (
    <svg
      viewBox={`0 0 ${candles.length * (w + gap)} 130`}
      className="w-full h-full"
      preserveAspectRatio="none"
    >
      <defs>
        <linearGradient id="merVolGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#0D9488" stopOpacity="0.3" />
          <stop offset="100%" stopColor="#0D9488" stopOpacity="0" />
        </linearGradient>
      </defs>
      {/* Grid lines */}
      {[30, 60, 90].map((y) => (
        <line
          key={y}
          x1="0"
          y1={y}
          x2={candles.length * (w + gap)}
          y2={y}
          stroke="#E5E7EB"
          strokeWidth="0.5"
          strokeDasharray="4 4"
        />
      ))}
      {candles.map((c, i) => {
        const x = i * (w + gap);
        const isGreen = c.c >= c.o;
        const bodyTop = 130 - Math.max(c.o, c.c);
        const bodyH = Math.abs(c.c - c.o) || 1;
        const wickTop = 130 - c.h;
        const wickBot = 130 - c.l;
        const color = isGreen ? '#0D9488' : '#EA580C';
        return (
          <g key={i}>
            <line
              x1={x + w / 2}
              y1={wickTop}
              x2={x + w / 2}
              y2={wickBot}
              stroke={color}
              strokeWidth="1.5"
            />
            <rect x={x + 2} y={bodyTop} width={w - 4} height={bodyH} fill={color} rx="1" />
          </g>
        );
      })}
      {/* Trend line */}
      <path
        d={`M0,${130 - candles[0].c} ${candles.map((c, i) => `L${i * (w + gap) + w / 2},${130 - c.c}`).join(' ')}`}
        fill="none"
        stroke="#0D9488"
        strokeWidth="1"
        opacity="0.4"
        strokeDasharray="3 3"
      />
    </svg>
  );
}

/* ───────────────────────── PAGE ───────────────────────── */

export default function MeridianPage() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const reviewsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', h, { passive: true });
    return () => window.removeEventListener('scroll', h);
  }, []);

  const scrollReviews = (dir: number) => {
    if (reviewsRef.current) reviewsRef.current.scrollBy({ left: dir * 360, behavior: 'smooth' });
  };

  const navLinks = [
    { label: 'Markets', href: '#markets' },
    { label: 'Platform', href: '#platform' },
    { label: 'Company', href: '#company' },
    { label: 'Education', href: '#education' },
    { label: 'Reviews', href: '#reviews' },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* ── Back to gallery ── */}
      <a
        href="/templates/"
        className="fixed bottom-5 left-1/2 -translate-x-1/2 md:bottom-auto md:top-4 md:left-4 md:translate-x-0 z-[200] flex items-center gap-1.5 bg-black/70 hover:bg-black/90 backdrop-blur-sm text-white text-xs font-semibold px-4 py-2 rounded-full transition-all duration-200 shadow-xl border border-white/10"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M19 12H5M12 5l-7 7 7 7" />
        </svg>
        <span className="hidden sm:inline">All Templates</span>
        <span className="sm:hidden">Templates</span>
      </a>

      {/* ── RISK DISCLAIMER ── */}
      <div className="bg-[#0D9488] text-white text-[11px] py-1.5 px-4 text-center">
        Trading CFDs involves significant risk.{' '}
        <strong>74% of retail investor accounts lose money.</strong> Consider whether you can afford
        the risk.
      </div>

      {/* ── HEADER ── */}
      <header
        className={`sticky top-0 z-50 transition-all duration-300 border-b ${
          scrolled
            ? 'bg-white/98 backdrop-blur-md shadow-sm border-[#E5E7EB]'
            : 'bg-white border-transparent'
        }`}
      >
        <div className="site-container flex items-center justify-between h-16">
          <Link href="/templates/meridian" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-[#0D9488] flex items-center justify-center">
              <Globe className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold text-[#111827]">
              Meridian<span className="text-[#0D9488]">FX</span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-7">
            {navLinks.map((l) => (
              <a
                key={l.label}
                href={l.href}
                className="text-[13px] font-medium text-[#6B7280] hover:text-[#111827] transition-colors"
              >
                {l.label}
              </a>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-3">
            <Link href="#" className="text-[13px] font-medium text-[#6B7280] hover:text-[#111827]">
              Log In
            </Link>
            <Link
              href="#"
              className="bg-[#EA580C] hover:bg-[#C2410C] text-white text-[13px] font-bold px-6 py-2.5 rounded-lg transition-colors"
            >
              Open Account
            </Link>
          </div>

          <button
            className="lg:hidden p-2 text-[#111827]"
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        <AnimatePresence>
          {mobileOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="lg:hidden bg-white border-t border-[#E5E7EB] overflow-hidden"
            >
              <div className="site-container py-4 flex flex-col gap-3">
                {navLinks.map((l) => (
                  <a
                    key={l.label}
                    href={l.href}
                    className="text-sm text-[#6B7280] py-2"
                    onClick={() => setMobileOpen(false)}
                  >
                    {l.label}
                  </a>
                ))}
                <Link
                  href="#"
                  className="bg-[#EA580C] text-white text-sm font-bold py-2.5 rounded-lg text-center mt-2"
                >
                  Open Account
                </Link>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      {/* ── HERO — with "photo" background ── */}
      <section className="relative overflow-hidden">
        {/* Background image placeholder — professional trading floor gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#111827] via-[#1F2937] to-[#0D3D38]">
          {/* Simulated office/trading floor photo overlay with geometric grid */}
          <div className="absolute inset-0 opacity-10">
            {Array.from({ length: 8 }).map((_, row) =>
              Array.from({ length: 12 }).map((_, col) => (
                <div
                  key={`${row}-${col}`}
                  className="absolute w-16 h-12 border border-white/20 rounded-sm"
                  style={{
                    left: `${col * 9}%`,
                    top: `${row * 13}%`,
                    opacity: 0.3 + Math.random() * 0.4,
                  }}
                >
                  {/* Mini chart lines suggesting monitor screens */}
                  <svg viewBox="0 0 60 40" className="w-full h-full">
                    <path
                      d={`M0,${25 + Math.random() * 10} Q15,${15 + Math.random() * 15} 30,${10 + Math.random() * 15} T60,${5 + Math.random() * 20}`}
                      fill="none"
                      stroke={Math.random() > 0.5 ? '#0D9488' : '#EA580C'}
                      strokeWidth="1"
                      opacity="0.6"
                    />
                  </svg>
                </div>
              )),
            )}
          </div>
          {/* Gradient overlay for readability */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#111827]/95 via-[#111827]/80 to-transparent" />
        </div>

        <div className="site-container relative z-10 py-20 md:py-28">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Left */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/10 rounded-full px-4 py-1.5 mb-6">
                <ShieldCheck className="w-4 h-4 text-[#0D9488]" />
                <span className="text-white/80 text-xs">Regulated across 7 jurisdictions</span>
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-[56px] font-bold text-white leading-tight mb-6">
                Trade Smarter,
                <br />
                Not Harder.
              </h1>
              <p className="text-white/70 text-lg mb-8 max-w-md leading-relaxed">
                Join 25,000+ traders who trust MeridianFX for institutional-grade execution, tight
                spreads, and world-class support.
              </p>
              <div className="flex flex-wrap gap-4 mb-10">
                <Link
                  href="#"
                  className="bg-[#EA580C] hover:bg-[#C2410C] text-white font-bold px-8 py-3.5 rounded-lg transition-colors inline-flex items-center gap-2"
                >
                  Start Trading <ArrowRight className="w-4 h-4" />
                </Link>
                <Link
                  href="#"
                  className="bg-white/10 backdrop-blur-sm border border-white/20 text-white font-semibold px-8 py-3.5 rounded-lg hover:bg-white/15 transition-colors inline-flex items-center gap-2"
                >
                  <Play className="w-4 h-4" /> Watch Demo
                </Link>
              </div>
              {/* Trust row */}
              <div className="flex flex-wrap items-center gap-5">
                <div className="flex items-center gap-2">
                  <Star className="w-5 h-5 fill-[#00B67A] text-[#00B67A]" />
                  <span className="text-white text-xs font-bold">Trustpilot</span>
                  <div className="flex gap-0.5">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <div
                        key={i}
                        className="w-[18px] h-[18px] bg-[#00B67A] flex items-center justify-center"
                      >
                        <Star className="w-3 h-3 fill-white text-white" />
                      </div>
                    ))}
                  </div>
                  <span className="text-white/60 text-xs">4.8</span>
                </div>
                <div className="w-px h-6 bg-white/20" />
                <div className="flex gap-1">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <div
                      key={i}
                      className="w-5 h-5 bg-[#FBBF24] flex items-center justify-center rounded-full"
                    >
                      <Star className="w-3 h-3 fill-white text-white" />
                    </div>
                  ))}
                  <span className="text-white/60 text-xs ml-1">Google 4.9</span>
                </div>
              </div>
            </motion.div>

            {/* Right — Chart panel */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="hidden md:block"
            >
              <div className="bg-white rounded-2xl shadow-2xl shadow-black/20 overflow-hidden">
                {/* Chart header */}
                <div className="flex items-center justify-between px-5 py-3 border-b border-[#E5E7EB]">
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-bold text-[#111827]">EUR/USD</span>
                    <span className="text-xs text-[#0D9488] font-semibold">1.0847</span>
                    <span className="text-[10px] bg-[#0D9488]/10 text-[#0D9488] px-2 py-0.5 rounded font-medium">
                      +0.12%
                    </span>
                  </div>
                  <div className="flex gap-2 text-[10px] text-[#6B7280]">
                    <span className="px-2 py-0.5 rounded bg-[#0D9488]/10 text-[#0D9488] font-medium">
                      1H
                    </span>
                    <span className="px-2 py-0.5">4H</span>
                    <span className="px-2 py-0.5">1D</span>
                    <span className="px-2 py-0.5">1W</span>
                  </div>
                </div>
                {/* Candlestick chart */}
                <div className="h-[200px] px-4 py-3">
                  <CandlestickChart />
                </div>
                {/* Quick trade */}
                <div className="px-5 pb-4 flex gap-3">
                  <button className="flex-1 bg-[#0D9488] hover:bg-[#0F766E] text-white font-bold py-2.5 rounded-lg text-sm transition-colors">
                    BUY 1.0848
                  </button>
                  <button className="flex-1 bg-[#EA580C] hover:bg-[#C2410C] text-white font-bold py-2.5 rounded-lg text-sm transition-colors">
                    SELL 1.0846
                  </button>
                </div>
                {/* Mini instruments */}
                <div className="border-t border-[#E5E7EB] px-5 py-3 flex gap-4">
                  {[
                    { s: 'Gold', p: '2,987.50', c: '+0.45%' },
                    { s: 'BTC/USD', p: '87,245', c: '+2.34%' },
                    { s: 'S&P 500', p: '5,842.3', c: '+0.32%' },
                  ].map((t) => (
                    <div key={t.s} className="flex-1 text-center">
                      <p className="text-[10px] text-[#6B7280]">{t.s}</p>
                      <p className="text-xs font-bold text-[#111827]">{t.p}</p>
                      <p className="text-[10px] text-[#0D9488]">{t.c}</p>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── STATS BAR ── */}
      <section className="bg-[#0D9488] py-8">
        <div className="site-container">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {stats.map((s, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="text-center"
              >
                <p className="text-3xl md:text-4xl font-bold text-white">{s.value}</p>
                <p className="text-[#B2DFDB] text-sm">{s.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── TEAM / PEOPLE SECTION ── */}
      <section className="site-section bg-[#F9FAFB]">
        <div className="site-container">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Left — "Photo" grid */}
            <div className="grid grid-cols-2 gap-3">
              {teamPhotos.map((p, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, scale: 0.95 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className={`relative rounded-xl overflow-hidden ${i === 0 ? 'row-span-2 h-full min-h-[280px]' : 'h-[136px]'}`}
                >
                  <div className={`absolute inset-0 bg-gradient-to-br ${p.gradient}`}>
                    {/* Person silhouette suggestion */}
                    <div className="absolute inset-0 flex items-end justify-center pb-4">
                      <div className="text-center">
                        <div className="w-16 h-16 rounded-full bg-white/20 backdrop-blur-sm mx-auto mb-2 flex items-center justify-center">
                          <span className="text-white text-xl font-bold">{p.initials}</span>
                        </div>
                        <p className="text-white/90 text-xs font-semibold">{p.role}</p>
                      </div>
                    </div>
                    {/* Subtle pattern overlay */}
                    <div className="absolute inset-0 opacity-10">
                      <svg viewBox="0 0 200 200" className="w-full h-full">
                        <pattern
                          id={`dots${i}`}
                          x="0"
                          y="0"
                          width="20"
                          height="20"
                          patternUnits="userSpaceOnUse"
                        >
                          <circle cx="2" cy="2" r="1" fill="white" />
                        </pattern>
                        <rect fill={`url(#dots${i})`} width="200" height="200" />
                      </svg>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Right — Text */}
            <div>
              <p className="text-[#0D9488] text-sm font-semibold uppercase tracking-wide mb-3">
                Our People
              </p>
              <h2 className="text-3xl md:text-4xl font-bold text-[#111827] mb-5">
                A Team of Market
                <br />
                Experts Behind You
              </h2>
              <p className="text-[#6B7280] leading-relaxed mb-6">
                At MeridianFX, your account is managed by real trading professionals — not chatbots.
                Our analysts, risk managers, and client success specialists work together to help
                you achieve your trading goals.
              </p>
              <div className="space-y-4 mb-8">
                {[
                  'Dedicated account manager for every client',
                  'Daily market analysis and trading signals',
                  'Weekly live webinars with senior analysts',
                  '24/5 multilingual support team',
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <Check className="w-5 h-5 text-[#0D9488] flex-shrink-0" />
                    <span className="text-[#374151] text-sm">{item}</span>
                  </div>
                ))}
              </div>
              <Link
                href="#"
                className="bg-[#EA580C] hover:bg-[#C2410C] text-white font-bold px-6 py-3 rounded-lg transition-colors inline-flex items-center gap-2"
              >
                Meet the Team <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ── FEATURES GRID ── */}
      <section className="site-section bg-white">
        <div className="site-container">
          <div className="text-center mb-12">
            <p className="text-[#0D9488] text-sm font-semibold uppercase tracking-wide mb-3">
              Why MeridianFX
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-[#111827]">
              Built for Serious Traders
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((f, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="bg-[#F9FAFB] rounded-xl p-7 border border-[#E5E7EB] hover:border-[#0D9488]/30 hover:shadow-md transition-all group"
              >
                <div className="w-12 h-12 rounded-xl bg-[#0D9488]/10 flex items-center justify-center mb-4 group-hover:bg-[#0D9488] transition-colors">
                  <f.icon className="w-6 h-6 text-[#0D9488] group-hover:text-white transition-colors" />
                </div>
                <h3 className="text-lg font-bold text-[#111827] mb-2">{f.title}</h3>
                <p className="text-[#6B7280] text-sm leading-relaxed">{f.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── LIVE CHART SECTION (dark) ── */}
      <section className="bg-[#111827] py-20" id="platform">
        <div className="site-container">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Left — Large chart */}
            <div>
              <div className="bg-[#1F2937] rounded-2xl border border-white/5 overflow-hidden">
                <div className="flex items-center justify-between px-5 py-3 border-b border-white/5">
                  <div className="flex items-center gap-4">
                    <span className="text-white text-sm font-bold">Gold / XAU</span>
                    <span className="text-[#0D9488] text-xs font-semibold">$2,987.50</span>
                  </div>
                  <div className="flex gap-1">
                    {['1m', '5m', '15m', '1H', '4H', '1D'].map((tf, i) => (
                      <span
                        key={i}
                        className={`text-[9px] px-2 py-0.5 rounded ${i === 3 ? 'bg-[#0D9488] text-white' : 'text-[#6B7280]'}`}
                      >
                        {tf}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="h-[240px] p-4">
                  <CandlestickChart />
                </div>
                {/* Volume bars */}
                <div className="h-[40px] px-4 pb-3 flex items-end gap-[2px]">
                  {candles.map((_, i) => (
                    <div
                      key={i}
                      className="flex-1 bg-[#0D9488]/30 rounded-t-sm"
                      style={{ height: `${15 + Math.random() * 25}px` }}
                    />
                  ))}
                </div>
                {/* Order book mini */}
                <div className="border-t border-white/5 px-5 py-3 grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-[10px] text-[#6B7280] mb-1">Bid</p>
                    {[2987.4, 2987.35, 2987.3].map((p, i) => (
                      <div key={i} className="flex justify-between text-xs">
                        <span className="text-[#0D9488]">{p.toFixed(2)}</span>
                        <div className="w-16 h-1.5 bg-[#0D9488]/20 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-[#0D9488]/50 rounded-full"
                            style={{ width: `${60 + i * 15}%` }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                  <div>
                    <p className="text-[10px] text-[#6B7280] mb-1">Ask</p>
                    {[2987.6, 2987.65, 2987.7].map((p, i) => (
                      <div key={i} className="flex justify-between text-xs">
                        <span className="text-[#EA580C]">{p.toFixed(2)}</span>
                        <div className="w-16 h-1.5 bg-[#EA580C]/20 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-[#EA580C]/50 rounded-full"
                            style={{ width: `${70 - i * 15}%` }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            {/* Right — Text */}
            <div>
              <p className="text-[#0D9488] text-sm font-semibold uppercase tracking-wide mb-3">
                Advanced Charts
              </p>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-5">
                Professional-Grade
                <br />
                Trading Tools
              </h2>
              <p className="text-[#9CA3AF] leading-relaxed mb-8">
                Real-time candlestick charts, 100+ technical indicators, drawing tools, and
                depth-of-market visualization. Everything you need to make informed trading
                decisions.
              </p>
              <div className="grid grid-cols-2 gap-4 mb-8">
                {[
                  { icon: BarChart3, label: '100+ Indicators' },
                  { icon: LineChart, label: 'Multiple Timeframes' },
                  { icon: Wifi, label: 'Real-Time Data' },
                  { icon: Smartphone, label: 'Mobile Sync' },
                ].map((f, i) => (
                  <div key={i} className="flex items-center gap-3 bg-white/5 rounded-lg px-4 py-3">
                    <f.icon className="w-5 h-5 text-[#0D9488]" />
                    <span className="text-white text-sm font-medium">{f.label}</span>
                  </div>
                ))}
              </div>
              <div className="flex gap-3">
                <Link
                  href="#"
                  className="bg-[#EA580C] hover:bg-[#C2410C] text-white font-bold px-6 py-3 rounded-lg transition-colors"
                >
                  Try Platform
                </Link>
                <Link
                  href="#"
                  className="border border-white/20 text-white font-semibold px-6 py-3 rounded-lg hover:bg-white/5 transition-colors"
                >
                  View Demo
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── MARKETS ── */}
      <section className="site-section bg-white" id="markets">
        <div className="site-container">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#111827]">Trade Global Markets</h2>
            <p className="text-[#6B7280] mt-2">
              Access thousands of instruments across 5 asset classes
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {markets.map((m, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="text-center bg-[#F9FAFB] rounded-xl p-6 border border-[#E5E7EB] hover:border-[#0D9488]/30 hover:shadow-md transition-all cursor-pointer"
              >
                <span className="text-3xl block mb-3">{m.icon}</span>
                <h3 className="font-bold text-[#111827] mb-1">{m.name}</h3>
                <p className="text-xs text-[#6B7280]">{m.instruments}</p>
                <p className="text-xs text-[#0D9488] font-medium mt-1">{m.spread}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── REVIEWS — PROMINENT ── */}
      <section className="site-section bg-[#F9FAFB]" id="reviews">
        <div className="site-container">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-10">
            <div>
              <p className="text-[#0D9488] text-sm font-semibold uppercase tracking-wide mb-2">
                Reviews
              </p>
              <h2 className="text-3xl md:text-4xl font-bold text-[#111827]">
                What Our Traders Say
              </h2>
              <div className="flex items-center gap-4 mt-4">
                <div className="flex items-center gap-2">
                  <Star className="w-6 h-6 fill-[#00B67A] text-[#00B67A]" />
                  <span className="text-sm font-bold text-[#111827]">Trustpilot</span>
                  <span className="text-sm text-[#6B7280]">4.8 / 5</span>
                </div>
                <div className="w-px h-5 bg-[#D1D5DB]" />
                <div className="flex items-center gap-2">
                  <div className="w-5 h-5 bg-[#FBBF24] rounded-full flex items-center justify-center">
                    <Star className="w-3 h-3 fill-white text-white" />
                  </div>
                  <span className="text-sm font-bold text-[#111827]">Google</span>
                  <span className="text-sm text-[#6B7280]">4.9 / 5</span>
                </div>
              </div>
            </div>
            <div className="flex gap-2 mt-4 md:mt-0">
              <button
                onClick={() => scrollReviews(-1)}
                className="w-10 h-10 rounded-full border border-[#D1D5DB] flex items-center justify-center text-[#6B7280] hover:text-[#111827] hover:border-[#111827] transition-colors"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                onClick={() => scrollReviews(1)}
                className="w-10 h-10 rounded-full border border-[#D1D5DB] flex items-center justify-center text-[#6B7280] hover:text-[#111827] hover:border-[#111827] transition-colors"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>

          <div
            ref={reviewsRef}
            className="flex gap-5 overflow-x-auto pb-4"
            style={{ scrollbarWidth: 'none' }}
          >
            {reviews.map((r, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.06 }}
                className="flex-shrink-0 w-[340px] bg-white rounded-xl border border-[#E5E7EB] p-6 hover:shadow-md transition-shadow"
              >
                {/* Source badge */}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex gap-0.5">
                    {Array.from({ length: r.stars }).map((_, si) => (
                      <div
                        key={si}
                        className="w-5 h-5 bg-[#00B67A] flex items-center justify-center"
                      >
                        <Star className="w-3 h-3 fill-white text-white" />
                      </div>
                    ))}
                  </div>
                  <span
                    className={`text-[10px] font-bold px-2 py-0.5 rounded ${r.source === 'Trustpilot' ? 'bg-[#00B67A]/10 text-[#00B67A]' : 'bg-[#FBBF24]/10 text-[#D97706]'}`}
                  >
                    {r.source}
                  </span>
                </div>
                <p className="text-[#374151] text-sm leading-relaxed mb-5">
                  &ldquo;{r.text}&rdquo;
                </p>
                <div className="flex items-center gap-3 border-t border-[#F3F4F6] pt-4">
                  {/* Photo-style avatar */}
                  <div className="w-11 h-11 rounded-full bg-gradient-to-br from-[#0D9488] to-[#115E59] flex items-center justify-center shadow-inner">
                    <span className="text-white text-sm font-bold">{r.avatar}</span>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-[#111827]">{r.name}</p>
                    <p className="text-xs text-[#6B7280]">{r.role}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── REGULATION ── */}
      <section className="site-section bg-white" id="company">
        <div className="site-container">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#111827]">Regulated Worldwide</h2>
            <p className="text-[#6B7280] mt-2">
              Your funds are protected by top-tier regulators across 7 jurisdictions
            </p>
          </div>
          <div className="flex flex-wrap justify-center gap-4 mb-10">
            {regulations.map((r, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.06 }}
                className="flex items-center gap-3 bg-[#F9FAFB] border border-[#E5E7EB] rounded-xl px-5 py-4 hover:border-[#0D9488]/30 transition-colors"
              >
                <div className="w-10 h-10 rounded-full bg-[#0D9488] flex items-center justify-center flex-shrink-0">
                  <span className="text-white text-xs font-bold">{r.code}</span>
                </div>
                <div>
                  <p className="text-sm font-bold text-[#0D9488]">{r.name}</p>
                  <p className="text-[10px] text-[#6B7280] max-w-[180px]">{r.full}</p>
                </div>
              </motion.div>
            ))}
          </div>
          {/* Trusted by logos */}
          <div className="border-t border-[#E5E7EB] pt-8">
            <p className="text-center text-sm text-[#6B7280] mb-5">Trusted & Secured By</p>
            <div className="flex flex-wrap items-center justify-center gap-10">
              {['GeoTrust', 'McAfee', 'BARCLAYS', 'Trustpilot', 'SSL Secure'].map((l) => (
                <span key={l} className="text-[#9CA3AF] text-sm font-bold tracking-wider">
                  {l}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="site-section bg-[#F9FAFB]">
        <div className="site-container">
          <h2 className="text-2xl font-bold text-[#111827] mb-8">Frequently Asked Questions</h2>
          <div className="max-w-3xl space-y-3">
            {faqs.map((f, i) => (
              <div key={i} className="bg-white rounded-xl border border-[#E5E7EB] overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between p-5 text-left"
                >
                  <span className="font-semibold text-[#111827] text-sm">{f.q}</span>
                  <ChevronDown
                    className={`w-5 h-5 text-[#0D9488] flex-shrink-0 transition-transform duration-300 ${openFaq === i ? 'rotate-180' : ''}`}
                  />
                </button>
                <AnimatePresence>
                  {openFaq === i && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      <p className="px-5 pb-5 text-sm text-[#6B7280] leading-relaxed">{f.a}</p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="bg-[#0D9488] py-16">
        <div className="site-container text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to Start Trading?
          </h2>
          <p className="text-[#B2DFDB] text-lg mb-8 max-w-lg mx-auto">
            Open your account in minutes and join 25,000+ traders worldwide.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              href="#"
              className="bg-white text-[#0D9488] hover:bg-[#F0FDFA] font-bold px-8 py-3.5 rounded-lg transition-colors inline-flex items-center gap-2"
            >
              Open Live Account <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              href="#"
              className="border border-white/30 text-white hover:bg-white/10 font-semibold px-8 py-3.5 rounded-lg transition-colors"
            >
              Try Free Demo
            </Link>
          </div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="bg-[#111827] pt-14 pb-6">
        <div className="site-container">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-8 mb-10">
            {Object.entries(footerLinks).map(([cat, links]) => (
              <div key={cat}>
                <h4 className="text-white font-semibold text-sm mb-4">{cat}</h4>
                <ul className="space-y-2">
                  {links.map((l) => (
                    <li key={l.label}>
                      <a
                        href={l.href}
                        className="text-[#9CA3AF] text-xs hover:text-white transition-colors"
                      >
                        {l.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div className="border-t border-white/10 pt-8 mb-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-md bg-[#0D9488] flex items-center justify-center">
                <Globe className="w-4 h-4 text-white" />
              </div>
              <span className="text-white font-bold">
                Meridian<span className="text-[#0D9488]">FX</span>
              </span>
            </div>
            <div className="flex gap-3">
              {[
                {
                  color: '#FF0000',
                  path: 'M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z',
                },
                {
                  color: '#E4405F',
                  path: 'M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z',
                },
                {
                  color: '#1DA1F2',
                  path: 'M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z',
                },
                {
                  color: '#0A66C2',
                  path: 'M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z',
                },
                {
                  color: '#0088CC',
                  path: 'M11.944 0A12 12 0 000 12a12 12 0 0012 24 12 12 0 0012-12A12.02 12.02 0 0012 0h-.056zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 01.171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z',
                },
              ].map((s, i) => (
                <a
                  key={i}
                  href="#"
                  className="w-9 h-9 rounded-full flex items-center justify-center transition-opacity hover:opacity-80"
                  style={{ backgroundColor: s.color }}
                >
                  <svg className="w-4 h-4 fill-white" viewBox="0 0 24 24">
                    <path d={s.path} />
                  </svg>
                </a>
              ))}
            </div>
          </div>
          <div className="border-t border-white/5 pt-6">
            <p className="text-[10px] text-[#6B7280] leading-relaxed mb-3">
              <strong className="text-[#9CA3AF]">Risk Warning:</strong> Trading Forex and CFDs
              involves significant risk and can result in the loss of your invested capital. You
              should not invest more than you can afford to lose.{' '}
              <strong className="text-[#9CA3AF]">
                74% of retail investor accounts lose money when trading CFDs with this provider.
              </strong>
            </p>
            <div className="flex flex-wrap gap-4 text-[10px] text-[#6B7280]">
              <a href="#" className="hover:text-white transition-colors">
                Terms & Conditions
              </a>
              <a href="#" className="hover:text-white transition-colors">
                Privacy Policy
              </a>
              <a href="#" className="hover:text-white transition-colors">
                Risk Disclosure
              </a>
              <a href="#" className="hover:text-white transition-colors">
                Cookie Policy
              </a>
            </div>
            <p className="text-[10px] text-[#4B5563] mt-4">
              &copy; 2026 MeridianFX Ltd. All rights reserved.
            </p>
          </div>
        </div>
      </footer>

      {/* ── BOTTOM TICKER ── */}
      <div className="bg-[#0B0F1A] border-t border-white/5 py-2.5 overflow-hidden">
        <div className="ticker-animate flex gap-8 whitespace-nowrap">
          {[...tickers, ...tickers].map((t, i) => (
            <span key={i} className="inline-flex items-center gap-2 text-xs">
              <span className="font-bold text-white/70">{t.symbol}</span>
              <span className="text-[#6B7280]">{t.price}</span>
              <span className={t.change.startsWith('+') ? 'text-[#0D9488]' : 'text-[#EA580C]'}>
                {t.change}
              </span>
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
